import { useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { useFetchReleaseTracksQuery } from "../../../../api/releases/hooks/useFetchReleaseTracksQuery";
import { SupplyChainAppAxios } from "../../../../api/supplyChain/supplyChainAxiosInstance";
import { editReleaseTrackFormAtom } from "../../../../atoms/forms/editReleaseTrackFormAtom";
import { uploadSidePanelAtom } from "../../../../atoms/location/uploadSidePanelAtom";
import { uploadReleaseTrackStateAtom } from "../../../../atoms/releases/uploadReleaseTrackAtom";
import useGetSupplyDashboardOptionsQuery from "../../../../hooks/supplyChainHooks/useGetSupplyDashboardOptionsQuery";
import { convertCommaSeparatedToReadable } from "../../../../utils/utils";
import { ErrorScreen } from "../../../screens/Error/ErrorScreen";
import LoadingScreen from "../../LoadingScreen/LoadingScreen";
import { EditReleaseTrackForm } from "./EditReleaseTrackForm";
import { EditReleaseTrackHeader } from "./components/EditReleaseTrackHeader";

interface WrappedEditReleaseTrackFormProps {
  handleCancel: () => void;
  releaseTrackId: number | undefined | null;
  releaseId: number | undefined;
  handleSave: () => void;
  handleSelectTrack: (releaseTrackId: string) => void;
}

export const WrappedEditReleaseTrackForm = ({
  handleCancel,
  releaseTrackId,
  releaseId,
  handleSave,
  handleSelectTrack,
}: WrappedEditReleaseTrackFormProps) => {
  // Fetch Supply Chain-specific options
  const { data: dashboardOptions, isPending: isSupplyChainOptionsPending } =
    useGetSupplyDashboardOptionsQuery(SupplyChainAppAxios.client);
  const setFormState = useSetAtom(editReleaseTrackFormAtom);
  const setSidePanelOpen = useSetAtom(uploadSidePanelAtom);

  const { songTitle } = useAtomValue(uploadReleaseTrackStateAtom);

  // Fetch all ReleaseTracks (to navigate to next/previous track)
  const { data: releaseTracks, isPending: isReleaseTracksPending } =
    useFetchReleaseTracksQuery(releaseId);

  const releaseTrack = useMemo(() => {
    if (!releaseTracks || !releaseTrackId) return null;
    return releaseTracks.find((track) => track.id === releaseTrackId);
  }, [releaseTracks, releaseTrackId]);

  const isLoading = useMemo(
    () =>
      (isSupplyChainOptionsPending && !dashboardOptions) ||
      isReleaseTracksPending,
    [isSupplyChainOptionsPending, dashboardOptions, isReleaseTracksPending],
  );

  const handleSelectReleaseTrack = useCallback(
    (index: number) => {
      if (!releaseTracks) {
        toast.error("Failed to find tracks, please refresh or try again.");
        return;
      }
      const track = releaseTracks.find((track) => track.track_number === index);
      if (!track?.id) {
        toast.error("Failed to find track, please refresh or try again.");
        return;
      }
      // Update URL with the new releaseTrackId (without closing the sidepanel)
      handleSelectTrack(track.id.toString());
    },
    [releaseTracks, handleSelectTrack],
  );

  useEffect(() => {
    if (!releaseTrack) return;

    // Pre-fill form with ReleaseTrack data or fallback to Release data
    const formValues = {
      songTitle: songTitle || releaseTrack.title || releaseTrack.release?.title,
      genre: String(
        releaseTrack.supply_chain_main_genre ||
          releaseTrack.release?.supply_chain_main_genre,
      ),
      secondaryGenre: String(
        releaseTrack.supply_chain_secondary_genre ||
          releaseTrack.release?.supply_chain_secondary_genre,
      ),
      language: String(
        releaseTrack.supply_chain_language ||
          releaseTrack.release?.supply_chain_language,
      ),
      parentalAdvisory: String(
        releaseTrack.parental_advisory ||
          releaseTrack.release?.parental_advisory,
      ),
      artistName: releaseTrack.release?.artist_display_name,
      featuredArtists: releaseTrack.featured_artists
        ? convertCommaSeparatedToReadable(releaseTrack.featured_artists)
        : "",
    };

    // Filter undefined values
    setFormState(
      Object.fromEntries(
        Object.entries(formValues).filter(([, value]) => value !== undefined),
      ),
    );
  }, [releaseTrack, setFormState, songTitle]);

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!dashboardOptions) {
    return (
      <ErrorScreen
        message={"Failed to fetch Supply Chain options. Please try again."}
      />
    );
  }

  if (!releaseTrackId) {
    return (
      <ErrorScreen
        message={"Failed to find Release Track. Please try again."}
      />
    );
  }

  return (
    <>
      <EditReleaseTrackHeader
        artistName={releaseTrack?.release?.artist_display_name ?? undefined}
        publishDate={releaseTrack?.release?.published_at ?? undefined}
        publishEndDate={releaseTrack?.release?.published_end ?? undefined}
        releaseCover={releaseTrack?.release?.release_cover ?? null}
        releaseTitle={releaseTrack?.release?.title}
        releaseTrackTitle={releaseTrack?.title}
        trackIndex={releaseTrack?.track_number ?? 0}
        numberOfTracks={releaseTracks?.length ?? 1}
        handleSelectTrack={handleSelectReleaseTrack}
      />
      <EditReleaseTrackForm
        handleCancel={handleCancel}
        handleSave={async () => {
          handleSave();
          setSidePanelOpen(false);
        }}
        releaseTrackId={releaseTrackId}
        genres={dashboardOptions.genres}
        languages={dashboardOptions.languages}
        parentalAdvisories={dashboardOptions.parental_advisories}
      />
    </>
  );
};
